import Decimal from 'decimal.js';
import { createContext } from 'preact';
import { useContext } from 'preact/hooks';
import { availableRoom } from '../engine/tfsa';
import { floorAmountCents } from './clamp';
import type { ResidencyYear, CRARoomMethod, TFSAState, ContributionEntry, WithdrawalEntry } from '../engine/types';

// ── Domain types ────────────────────────────────────────────────────────────

export interface WizardInputs {
  birth_year: number;
  residency_year: ResidencyYear;
  cra_room_method: CRARoomMethod;
  cra_room_initial_cents: string;
  /** ISO timestamp when user last entered CRA snapshot. Null on estimate path. */
  cra_verified_at_iso: string | null;
  contributions_ytd_cents: string;
  withdrawals_ytd_cents: string;
  /**
   * P0-1 wiring: optional excess-cents override. In v1 the wizard does
   * NOT collect this directly — buildTFSAState derives the excess from
   * the magnitude of negative availableRoom. Reserved for future tiers
   * or import flows that need to inject an explicit excess.
   */
  prior_year_excess_cents?: string;
  /**
   * P0-1 wiring: ISO-8601 date (YYYY-MM-DD) the over-contribution began.
   * Collected by the conditional wizard step shown when availableRoom
   * would be negative. Absent when user skipped the step or never
   * over-contributed; PenaltyBanner stays dormant in both cases.
   */
  prior_year_excess_started_iso?: string;
}

export interface LogEntry {
  id: number;
  type: 'contribution' | 'withdrawal';
  date: string;          // YYYY-MM-DD local clock
  amount_cents: string;
  institution?: string;
  created_at: string;    // ISO timestamp
}

export interface AppState {
  disclaimerAccepted: boolean;
  wizardCompleted: boolean;
  wizardInputs: WizardInputs | null;
  logEntries: LogEntry[];
  constantsVersion: string;
  constantsVerifiedAt: string;
}

// ── Initial state ────────────────────────────────────────────────────────────

export const initialAppState: AppState = {
  disclaimerAccepted: false,
  wizardCompleted: false,
  wizardInputs: null,
  logEntries: [],
  constantsVersion: 'v2026.1',
  constantsVerifiedAt: '2026-05-02',
};

// ── Actions ──────────────────────────────────────────────────────────────────

/**
 * Mutable fields on a logged entry. Type and id are fixed; created_at is
 * a write-once provenance stamp. Anything else the user can correct.
 * `institution` accepts explicit `undefined` so the editor can clear it.
 */
export interface LogEntryPatch {
  amount_cents?: string;
  date?: string;
  institution?: string | undefined;
}

export type Action =
  | { type: 'ACCEPT_DISCLAIMER' }
  | { type: 'COMPLETE_WIZARD'; inputs: WizardInputs }
  | { type: 'ADD_ENTRY'; entry: Omit<LogEntry, 'id' | 'created_at'> }
  | {
      type: 'UPDATE_ENTRY';
      id: number;
      entry_type: 'contribution' | 'withdrawal';
      patch: LogEntryPatch;
    }
  | { type: 'DELETE_ENTRY'; id: number; entry_type: 'contribution' | 'withdrawal' }
  | { type: 'UPDATE_CONSTANTS'; version: string; verifiedAt: string }
  | { type: 'HYDRATE'; state: AppState }
  | { type: 'RESET' };

export function appReducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'ACCEPT_DISCLAIMER':
      return { ...state, disclaimerAccepted: true };

    case 'COMPLETE_WIZARD':
      return { ...state, wizardCompleted: true, wizardInputs: action.inputs };

    case 'ADD_ENTRY': {
      const nextId = state.logEntries.length > 0
        ? Math.max(...state.logEntries.map((e) => e.id)) + 1
        : 1;
      const entry: LogEntry = {
        ...action.entry,
        // v1.0.4 P0 (defect C, layer 2 — reducer floor): a negative
        // amount_cents must never reach state. The input layer's clamp
        // catches keystrokes, the modal floors before submit, and the
        // engine floors on read — this layer is the last line of defense
        // before the value is persisted to IDB.
        amount_cents: floorAmountCents(action.entry.amount_cents),
        id: nextId,
        created_at: new Date().toISOString(),
      };
      return { ...state, logEntries: [...state.logEntries, entry] };
    }

    case 'HYDRATE':
      return action.state;

    case 'UPDATE_ENTRY':
      return {
        ...state,
        logEntries: state.logEntries.map((e) => {
          if (!(e.id === action.id && e.type === action.entry_type)) return e;
          const next: LogEntry = { ...e };
          if (action.patch.amount_cents !== undefined) {
            // v1.0.4 P0: floor on patch — same defense as ADD_ENTRY.
            next.amount_cents = floorAmountCents(action.patch.amount_cents);
          }
          if (action.patch.date !== undefined) {
            next.date = action.patch.date;
          }
          // Institution: explicit `undefined` in the patch means "clear it";
          // a string means "replace it"; absence means "leave alone". This
          // matches buildTFSAState's omit-when-undefined convention.
          if ('institution' in action.patch) {
            if (action.patch.institution === undefined) {
              delete (next as { institution?: string }).institution;
            } else {
              next.institution = action.patch.institution;
            }
          }
          return next;
        }),
      };

    case 'DELETE_ENTRY':
      return {
        ...state,
        logEntries: state.logEntries.filter(
          (e) => !(e.id === action.id && e.type === action.entry_type)
        ),
      };

    case 'UPDATE_CONSTANTS':
      return { ...state, constantsVersion: action.version, constantsVerifiedAt: action.verifiedAt };

    case 'RESET':
      return initialAppState;

    default:
      return state;
  }
}

// ── buildTFSAState ────────────────────────────────────────────────────────────

export function buildTFSAState(
  appState: AppState,
  todayIso: string
): TFSAState | null {
  if (!appState.wizardCompleted || !appState.wizardInputs) return null;

  const inputs = appState.wizardInputs;

  const backfillContrib: ContributionEntry[] = inputs.contributions_ytd_cents !== '0'
    ? [{ date: todayIso.split('T')[0], amount_cents: inputs.contributions_ytd_cents }]
    : [];
  const backfillWithdrawal: WithdrawalEntry[] = inputs.withdrawals_ytd_cents !== '0'
    ? [{ date: todayIso.split('T')[0], amount_cents: inputs.withdrawals_ytd_cents }]
    : [];

  const loggedContribs: ContributionEntry[] = appState.logEntries
    .filter((e) => e.type === 'contribution')
    .map(({ date, amount_cents, institution }) => ({
      date,
      amount_cents,
      ...(institution !== undefined ? { institution } : {}),
    }));
  const loggedWithdrawals: WithdrawalEntry[] = appState.logEntries
    .filter((e) => e.type === 'withdrawal')
    .map(({ date, amount_cents, institution }) => ({
      date,
      amount_cents,
      ...(institution !== undefined ? { institution } : {}),
    }));

  const base: TFSAState = {
    cumulative_room_cents: inputs.cra_room_initial_cents,
    cra_room_initial_cents: inputs.cra_room_initial_cents,
    cra_room_method: inputs.cra_room_method,
    cra_verified_at_iso: inputs.cra_verified_at_iso,
    contributions: [...backfillContrib, ...loggedContribs],
    withdrawals: [...backfillWithdrawal, ...loggedWithdrawals],
    current_date_iso: todayIso,
  };

  // P0-1 wiring: populate the over-contribution fields used by
  // runningPenalty() iff the wizard captured a started-ISO. Excess cents
  // is derived from the magnitude of negative availableRoom unless an
  // explicit value was provided on WizardInputs (reserved for future
  // import flows; the v1 wizard never sets it directly).
  if (inputs.prior_year_excess_started_iso) {
    const avail = new Decimal(availableRoom(base));
    let excessCents: string | undefined;
    if (inputs.prior_year_excess_cents !== undefined) {
      excessCents = inputs.prior_year_excess_cents;
    } else if (avail.lessThan(0)) {
      excessCents = avail.abs().toFixed(0);
    }
    if (excessCents !== undefined) {
      base.prior_year_excess_started_iso = inputs.prior_year_excess_started_iso;
      base.prior_year_excess_cents = excessCents;
    }
  }

  return base;
}

// ── Context ──────────────────────────────────────────────────────────────────

interface AppContextValue {
  state: AppState;
  dispatch: (action: Action) => void;
}

export const AppContext = createContext<AppContextValue>({
  state: initialAppState,
  dispatch: () => { /* noop until Provider mounts */ },
});

export function useAppState(): AppContextValue {
  return useContext(AppContext);
}
